import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'
import { Alert, IconSvg, PasswordMaskField, SelectBox } from 'components/common'
import { SocialLoginButtons, TermsOfServiceAndPrivacyBlock } from 'containers/common'
import { useAuth } from 'hooks/useAuth'
import { debounce } from 'lodash'
import { objectToSearchString } from 'serialize-query-params'
import { AuthResponse } from 'models/response'
import { UserGender, useAgreeToTermsAndConditionsMutation, useRecordReferralSignupMutation } from 'graphql/generated/scheme'
import { EXCLUDE_COUNTRIES, GENDERS } from 'utils/constants'
import useReactIpLocation from 'react-ip-details'
import { format, subYears } from 'date-fns'
import { useStore } from 'hooks/useStore'
import PhoneInput from 'components/atoms/phone-input'
import moment from 'moment-timezone'
import { DatePicker } from '@mui/x-date-pickers'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { Button } from 'components/atoms/button'
import { Turnstile } from 'components/atoms/turnstile'
import { useTurnstile } from 'hooks/useTurnstile'

// Validation schemas
const emailSchema = Yup.string()
	.transform((_value, originalValue) => originalValue?.toLowerCase())
	.email('Your email address is invalid. Please try again.')
	.required('Email is required')
const PASSWORD_MESSAGE =
	'Passwords must have at least 8 characters and include 2 of the following: 1 uppercase letter, 1 number, or 1 special character.'
const passwordSchema = Yup.string()
	.required(PASSWORD_MESSAGE)
	.min(8, PASSWORD_MESSAGE)
	.test('password-complexity', PASSWORD_MESSAGE, value => {
		if (!value) return false
		const hasUpper = /[A-Z]/.test(value)
		const hasDigit = /\d/.test(value)
		const hasSpecial = /[@$#!%*?&._-]/.test(value)
		const score = (hasUpper ? 1 : 0) + (hasDigit ? 1 : 0) + (hasSpecial ? 1 : 0)
		return score >= 2
	})
const validationSchema = Yup.object().shape({
	userName: emailSchema,
	password: passwordSchema,
	firstName: Yup.string().required('Your first name is invalid. Please try again.'),
	lastName: Yup.string().required('Your last name is invalid. Please try again.'),
	dateOfBirth: Yup.date()
			.required('Must enter date of birth')
			.typeError('Date of birth is required')
			.min(new Date(1930, 0, 1), 'Date of birth must be January 1, 1930 or later')
			.max(subYears(new Date(), 14), 'You must be at least 14 years old'),
	phoneNumber: Yup.string()
		.required('Your phone number is invalid. Please try again.')
		.test('isValidPhoneNumber', 'Your phone number is invalid. Please try again.', value => {
			return isValidPhoneNumber(value, { defaultCountry: 'US' })
		}),
	gender: Yup.string()
		.required('Choose valid gender')
		.oneOf([UserGender.Male, UserGender.Female, UserGender.Other], 'Choose valid gender'),
	website: Yup.string().optional(),
	fax: Yup.string().optional(),
})

interface SignUpFormProps {
	initialEmail?: string
	onSuccess?: (rs: AuthResponse) => void
	isModal?: boolean
	onSwitchToLogin?: () => void
	onEmailNotAvailable?: (email: string) => void
	inviteId?: string
	redirectTo?: string | null
}

export const SignUpForm = ({
	initialEmail,
	onSuccess,
	isModal = false,
	onSwitchToLogin,
	onEmailNotAvailable,
	inviteId,
	redirectTo = null,
}: SignUpFormProps) => {
	const email = initialEmail || ''
	const searchParamsString = objectToSearchString({ to: redirectTo })
	const { register, resendRegisterEmail, checkEmailAvailability } = useAuth()
	const turnstile = useTurnstile()
	const { currentTerms } = useStore()
	const [message, setMessage] = useState<any>(null)
	const [submitting, setSubmitting] = useState<boolean>(false)
	const [signUpSuccess, setSignUpSuccess] = useState<boolean>(false)
	const [loadingCheckEmailAvailability, setLoadingCheckEmailAvailability] = useState<boolean>(false)
	const [availableEmail, setAvailableEmail] = useState<any>(null)
	const [validPassword, setValidPassword] = useState<any>(null)
	const { ipResponse } = useReactIpLocation({ numberToConvert: 100 })
	const [ipCountryCode, setIpCountryCode] = useState<any>(undefined)
	const [loadingPage, setLoadingPage] = useState<boolean>(!!email && !isModal)
	const {
		control,
		register: registerField,
		handleSubmit,
		formState: { errors, isValid, isDirty },
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			userName: email ?? '',
		},
	})

	const [agreeToTermsAndConditions] = useAgreeToTermsAndConditionsMutation()
	const [recordReferralSignup] = useRecordReferralSignupMutation()

	const debounceDropDown = useCallback(
		debounce(nextValue => {
			const { emailAddress, isValidated } = nextValue
			if (isValidated) {
				setLoadingCheckEmailAvailability(true)
				return checkEmailAvailability(
					{ emailAddress },
					(data: any) => {
						setLoadingCheckEmailAvailability(false)
						setAvailableEmail(data)
						if (!data) {
							if (onEmailNotAvailable) {
								onEmailNotAvailable(emailAddress)
							}
						}
					},
					() => {
						setLoadingCheckEmailAvailability(false)
					}
				)
			}
		}, 1000),
		[onEmailNotAvailable]
	)

	useEffect(() => {
		const countryCode = /*ipResponse?.country_code?.toLowerCase() ||*/ 'us'

		setIpCountryCode(countryCode)
	}, [ipResponse])

	useEffect(() => {
		if (email && !isModal) {
			const checkEmail = async () => {
				const valid = await emailSchema.isValid(email)
				setLoadingPage(true)
				if (valid) {
					checkEmailAvailability(
						{ emailAddress: email },
						(data: any) => {
							setLoadingPage(false)
							setAvailableEmail(data)
							if (!data && onEmailNotAvailable) {
								onEmailNotAvailable(email)
							}
						},
						() => {
							setLoadingPage(false)
						}
					)
				} else {
					setAvailableEmail(null)
					setLoadingPage(false)
				}
			}
			checkEmail()
		}
	}, [email, isModal, onEmailNotAvailable])

	const handleInputEmailOnchange = async (e: any) => {
		const { value } = e.target
		setAvailableEmail(null)
		const valid = await emailSchema.isValid(value)
		debounceDropDown({ emailAddress: value, isValidated: valid })
	}

	const handleInputPasswordOnchange = async (e: any) => {
		const { value } = e.target
		setValidPassword(null)
		const valid = await passwordSchema.isValid(value)
		setValidPassword(valid)
	}
	const onSubmit = (values: any) => {
		const { firstName, lastName, website, fax, ...submitValue } = values
		// Honeypot: silently do nothing if bot fields are filled
		if (website || fax) {
			return
		}

		if (values['dateOfBirth']) {
			submitValue['dateOfBirth'] = format(values['dateOfBirth'], 'yyyy-MM-dd')
		}
		setSubmitting(true)
		setMessage(null)

		register(
			{ ...submitValue, agreeToTermsAndConditions: true, name: `${firstName} ${lastName}` },
			turnstile.token,
			(response: AuthResponse) => {
				const { version } = currentTerms
				agreeToTermsAndConditions({ variables: { version } })

				setSubmitting(false)
				turnstile.reset()
				setSignUpSuccess(true)
				setMessage({
					title: 'Check your inbox!',
					description: 'A verification email was sent to your inbox. Please confirm your email to login.',
					type: 'success',
				})

				if (onSuccess) {
					onSuccess(response)
				}

				const referralToken = sessionStorage.getItem('referral_token')
				if (referralToken) {
					recordReferralSignup({ variables: { token: referralToken } })
						.then(() => {
							sessionStorage.setItem(`referral_signup_done:${referralToken}`, '1')
						})
						.catch(() => {})
						.finally(() => {
							sessionStorage.removeItem('referral_token')
						})
				}
			},
			(error: string) => {
				setSubmitting(false)
				turnstile.reset()
				setMessage({
					description: error,
					type: 'danger',
				})
			}
		)
	}

	const resend = () => {
		setSubmitting(true)
		setMessage(null)

		resendRegisterEmail(
			() => {
				setSubmitting(false)
				setMessage({
					title: 'Resent! Check your inbox!',
					description: 'A verification email was resent to your inbox. Please confirm your email to login.',
					type: 'success',
				})
			},
			(error: string) => {
				setSubmitting(false)
				setMessage({
					description: error,
					type: 'danger',
				})
			}
		)
	}

	if (loadingPage) {
		return (
			<div className="flex justify-center items-center py-4 h-screen">
				<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="text-white" />
			</div>
		)
	}

	const getLoginUrl = () => {
		return `/auth/login${searchParamsString ? `?${searchParamsString}` : ''}`
	}

	return (
		<div className={clsx(isModal ? '' : 'w-full')}>
			<div className={clsx(isModal ? '' : 'w-full')}>
				{!isModal && <h1 className="text-3xl font-bold text-center text-white mb-4">Sign up</h1>}
				<Alert message={message} />
				{signUpSuccess ? (
					<div className="mt-16">
						<div className="text-center">
							<Button
								variant="primary"
								fullWidth
								size="lg"
								disabled={submitting}
								onClick={resend}
								className="flex items-center justify-center gap-1"
							>
								{submitting && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />}
								Resend
							</Button>
						</div>
					</div>
				) : (
					<>
						<div className="mb-4">
							<div className="text-center text-white">
								Already have an account?{' '}
								{isModal && onSwitchToLogin ? (
									<Button variant="link" onClick={onSwitchToLogin} className="inline-flex">
										Login
									</Button>
								) : (
									<Link to={getLoginUrl()} state={{ inviteId }} className="inline-flex">
										<Button variant="link">Login</Button>
									</Link>
								)}
							</div>
						</div>
						<form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
							{/* Honeypot fields: hidden from users, filled only by bots */}
							<div
								className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden"
								aria-hidden="true"
							>
								<label htmlFor="hp-website">Website</label>
								<input
									id="hp-website"
									type="text"
									{...registerField('website')}
									tabIndex={-1}
									autoComplete="off"
								/>
								<label htmlFor="hp-fax">Fax</label>
								<input
									id="hp-fax"
									type="text"
									{...registerField('fax')}
									tabIndex={-1}
									autoComplete="off"
								/>
							</div>
							<div className={clsx('space-y-4 text-left')}>
								{/* Email field */}
								<div>
									<div>
										<div className="relative">
											<input
												id="email"
												type="email"
												className="px-4 py-3 pr-16 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
												placeholder="Email address"
												{...registerField('userName')}
												onChange={handleInputEmailOnchange}
												autoComplete="off"
											/>
											<div className="absolute top-1/2 right-4 -mt-3 h-6 flex flex-col items-center justify-center">
												<IconSvg
													icon="check"
													className={clsx('text-secondary-green hidden', {
														'!block':
															availableEmail &&
															!loadingCheckEmailAvailability &&
															!errors.userName,
													})}
												/>
												<IconSvg
													icon="warning"
													className={clsx('text-secondary-red hidden', {
														'!block':
															availableEmail === false &&
															!loadingCheckEmailAvailability &&
															!errors.userName,
													})}
												/>
												<FontAwesomeIcon
													icon={['fas', 'spinner']}
													size="lg"
													spin
													className={clsx('text-white hidden', {
														'!block': loadingCheckEmailAvailability,
													})}
												/>
											</div>
										</div>
										{errors.userName && (
											<div className="text-secondary-red mt-2 text-sm">
												{errors.userName.message as string}
											</div>
										)}
										{availableEmail === false &&
											!loadingCheckEmailAvailability &&
											!errors.userName && (
												<div className="text-secondary-red mt-2">
													A Loyalink account already exist with this email address, please
													Login.
												</div>
											)}
									</div>
								</div>

								{/* Password + personal info */}
								<>
									<div>
										<div>
											<Controller
												control={control}
												name="password"
												render={({ field }) => (
													<PasswordMaskField
														id="password"
														className="px-4 py-3 pr-16 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
														placeholder="Password"
														value={field.value ?? ''}
														onChange={(e: any) => {
															field.onChange(e)
															handleInputPasswordOnchange(e)
														}}
														onBlur={field.onBlur}
														autoComplete="new-password"
													/>
												)}
											/>
											{validPassword === false && errors.password && (
												<div className="text-secondary-red mt-2 text-sm">
													{errors.password.message as string}
												</div>
											)}
										</div>
									</div>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<div>
												<input
													id="firstName"
													type="text"
													className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
													placeholder="First name"
													{...registerField('firstName')}
												/>
												{errors.firstName && (
													<div className="text-secondary-red mt-2 text-sm">
														{errors.firstName.message as string}
													</div>
												)}
											</div>
										</div>
										<div>
											<div>
												<input
													id="lastName"
													type="text"
													className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
													placeholder="Last name"
													{...registerField('lastName')}
												/>
												{errors.lastName && (
													<div className="text-secondary-red mt-2 text-sm">
														{errors.lastName.message as string}
													</div>
												)}
											</div>
										</div>
									</div>
									<div className="flex gap-4">
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<Controller
													control={control}
													name="gender"
													render={({ field }) => (
														<SelectBox
															options={GENDERS.filter(
																gender => gender.value !== UserGender.NotDisclosed
															)}
															placeholder="Gender"
															value={field.value}
															onChange={(option: any) => field.onChange(option.value)}
															className="!min-w-0 h-12"
														/>
													)}
												/>
												<Popover className="relative">
													<PopoverButton className="text-white">
														<FontAwesomeIcon
															icon={['fas', 'circle-question']}
															size="lg"
														/>
													</PopoverButton>
													<PopoverPanel
														transition
														className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-72 -translate-x-1/2 p-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in bg-secondary rounded-lg"
													>
														You can change who sees your gender on your profile later.
														Select Other to choose another gender or if you'd rather not
														say.
													</PopoverPanel>
												</Popover>
											</div>
											{errors.gender && (
												<div className="text-secondary-red mt-2 text-sm">
													{errors.gender.message as string}
												</div>
											)}
										</div>
										<div className="flex-1">
											<div className="flex items-center gap-2">
												<Controller
													control={control}
													name="dateOfBirth"
													render={({ field }) => (
														<div className="custom-react-datetime-picker-mui">
															<DatePicker
																value={field.value ? moment(field.value) : null}
																onChange={(date) => field.onChange(date ? date.toDate() : null)}
																referenceDate={moment(new Date(1990, 0, 1))}
																minDate={moment(new Date(1930, 0, 1))}
																maxDate={moment(subYears(new Date(), 14))}
																slotProps={{
																	desktopPaper: {
																		className:
																			'custom-react-datetime-picker-paper-mui start-date-time-picker',
																	},
																	mobilePaper: {
																		className:
																			'custom-react-datetime-picker-paper-mui mobile-mui start-date-time-picker',
																	},
																	yearButton: {
																		sx: {
																			color: '#ffffff',
																			'&:not(.Mui-selected)': { color: '#ffffff' },
																		},
																	},
																	textField: {
																		onBlur: field.onBlur,
																		sx: {
																			'& .MuiPickersSectionList-root': {
																				width: 'auto',
																			},
																		},
																	},
																}}
															/>
														</div>
													)}
												/>
												<Popover className="relative">
													<PopoverButton className="text-white">
														<FontAwesomeIcon
															icon={['fas', 'circle-question']}
															size="lg"
														/>
													</PopoverButton>
													<PopoverPanel
														transition
														className="absolute right-0 z-10 mt-5 flex w-screen max-w-72 p-4 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in bg-secondary rounded-lg"
													>
														Providing your birthday helps us provide a tailored
														experience for your age.
													</PopoverPanel>
												</Popover>
											</div>
											{errors.dateOfBirth && (
												<div className="text-secondary-red mt-2 text-sm">
													{errors.dateOfBirth.message as string}
												</div>
											)}
										</div>
									</div>
									<div>
										<div>
											<Controller
												control={control}
												name="phoneNumber"
												render={({ field }) => (
													<PhoneInput
														country={ipCountryCode}
														value={field.value}
														onChange={field.onChange}
														onBlur={field.onBlur}
														containerClass="custom-react-phone-input-sign-up"
														excludeCountries={EXCLUDE_COUNTRIES}
													/>
												)}
											/>
											{errors.phoneNumber && (
												<div className="text-secondary-red mt-2 text-sm">
													{errors.phoneNumber.message as string}
												</div>
											)}
										</div>
									</div>
								</>
							</div>

							{/* Turnstile + submit */}
							<>
								<div className="mt-4 flex justify-center">
									<Turnstile
										ref={turnstile.turnstileRef}
										onSuccess={turnstile.onSuccess}
										onError={turnstile.onError}
										onExpire={turnstile.onExpire}
										theme="dark"
									/>
								</div>
								<div className="mt-6">
									<Button
										variant="primary"
										fullWidth
										size="lg"
										type="submit"
										disabled={
											!(isValid && isDirty) ||
											submitting ||
											!(availableEmail && !loadingCheckEmailAvailability)
										}
										className="flex items-center justify-center gap-1"
									>
										{submitting && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />}
										Continue
									</Button>
								</div>
							</>

							<div className="mt-4">
								<TermsOfServiceAndPrivacyBlock
									label="By signing up, you agree to #termsOfService and #privacyPolicy."
									params={{
										termsOfService: 'Our Terms of Service',
										privacyPolicy: 'Privacy Policy',
									}}
								/>
							</div>
						</form>
						<div>
							<div className="mt-4">
								<h3 className="text-center text-white mb-6">Or Sign up via</h3>
								<SocialLoginButtons isModal={isModal} />
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	)
}
